/*
 * Longeva article enhancements (client-side, deferred).
 *
 *   1. Reformats the article date from US ("May 20, 2026") to UK ("20 May 2026").
 *   2. Injects a reading-time badge calculated from body word count.
 *   3. Injects a category eyebrow above the H1 from the article's first tag
 *      (sourced from the Article JSON-LD already in the page).
 *   4. Injects a breadcrumb (Home / Journal / Article) above the eyebrow.
 *   5. Appends a footer band with a "Back to the journal" link.
 *
 * Engineering choices:
 *   - Pure vanilla, no framework, no dependencies.
 *   - Single read-pass on DOM for selectors; cached references.
 *   - Date reformat runs synchronously (it is above-the-fold visible).
 *   - All other injections run inside requestIdleCallback (or setTimeout
 *     fallback) to avoid blocking INP.
 *   - Each enhancement is wrapped in try/catch so one failure does not
 *     break the others.
 *   - Honours prefers-reduced-motion (no animation here, but the gate
 *     is in place for future additions).
 *   - Idempotent: guarded against double-injection if the script ever
 *     runs twice (view-transitions, bfcache restore, etc).
 */
(function () {
  'use strict';

  if (typeof document === 'undefined') return;
  if (!document.body || !document.body.classList.contains('template-article')) return;

  // ---- Utilities -----------------------------------------------------------

  var idle = (typeof window.requestIdleCallback === 'function')
    ? function (cb) { window.requestIdleCallback(cb, { timeout: 1500 }); }
    : function (cb) { setTimeout(cb, 1); };

  function safe(fn) {
    try { fn(); } catch (_) { /* isolated; never blocks subsequent enhancements */ }
  }

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  // ---- 1. Date reformat ----------------------------------------------------
  // Runs synchronously so the visible date is correct on first paint.

  var MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  var MONTH_RE = MONTHS.join('|');
  var US_DATE_RE = new RegExp('\\b(' + MONTH_RE + ')\\s+(\\d{1,2}),\\s+(\\d{4})\\b');

  function rewriteUsDate(str) {
    return str.replace(US_DATE_RE, function (_, month, day, year) {
      return day.replace(/^0/, '') + ' ' + month + ' ' + year;
    });
  }

  safe(function () {
    var dateScopes = $$('time, .blog-post-info-text, ._blog-post-info-text, .article__date, [data-blog-post-details]');
    if (!dateScopes.length) return;
    var seen = new WeakSet();
    dateScopes.forEach(function (scope) {
      if (seen.has(scope)) return;
      seen.add(scope);
      var walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, null, false);
      var node;
      while ((node = walker.nextNode())) {
        var value = node.nodeValue;
        if (!US_DATE_RE.test(value)) continue;
        node.nodeValue = rewriteUsDate(value);
      }
    });
  });

  // ---- Read article metadata once (used by multiple enhancements) ----------

  function getArticleSchema() {
    var scripts = document.querySelectorAll('script[type="application/ld+json"]');
    for (var i = 0; i < scripts.length; i++) {
      try {
        var data = JSON.parse(scripts[i].textContent);
        if (data && data['@type'] === 'Article') return data;
      } catch (_) {}
    }
    return null;
  }

  function getFirstTag() {
    var schema = getArticleSchema();
    if (schema) {
      if (schema.articleSection) return String(schema.articleSection).split(',')[0].trim();
      if (schema.keywords) return String(schema.keywords).split(',')[0].trim();
    }
    var meta = document.querySelector('meta[property="article:tag"], meta[name="article:tag"]');
    if (meta && meta.content) return meta.content.split(',')[0].trim();
    return null;
  }

  function getH1() {
    return $('.template-article main h1') || $('main h1') || $('article h1') || $('h1');
  }

  function getArticleBody() {
    return $('._blog-post-content') || $('.article__body') || $('article .rte') || $('main .rte') || $('.rte');
  }

  // ---- 2-5. Non-critical injections (deferred to idle) ---------------------

  idle(function () {
    // 2. Reading time
    safe(function () {
      var body = getArticleBody();
      if (!body) return;
      var text = body.textContent || '';
      var words = text.trim().split(/\s+/).filter(Boolean).length;
      if (words < 40) return;
      var mins = Math.max(1, Math.round(words / 220)); // 220 wpm average reading speed
      var detailsTarget = $('.blog-post-info-text') || $('._blog-post-info-text') || $('[data-blog-post-details]');
      if (!detailsTarget) return;
      if (detailsTarget.querySelector('.longeva-article-readtime')) return; // idempotent
      var span = document.createElement('span');
      span.className = 'longeva-article-readtime';
      span.textContent = mins + ' min read';
      detailsTarget.appendChild(span);
    });

    // 3. Category eyebrow
    safe(function () {
      var h1 = getH1();
      if (!h1) return;
      if (h1.previousElementSibling && h1.previousElementSibling.classList && h1.previousElementSibling.classList.contains('longeva-article-eyebrow')) return;
      var tag = getFirstTag();
      if (!tag) return;
      var eyebrow = document.createElement('span');
      eyebrow.className = 'longeva-article-eyebrow';
      eyebrow.textContent = tag;
      h1.parentNode.insertBefore(eyebrow, h1);
    });

    // 4. Breadcrumb
    safe(function () {
      if (document.querySelector('.longeva-article-breadcrumb')) return;
      var h1 = getH1();
      if (!h1) return;
      var title = (h1.textContent || 'Article').trim();
      var crumb = document.createElement('nav');
      crumb.className = 'longeva-article-breadcrumb';
      crumb.setAttribute('aria-label', 'Breadcrumb');
      crumb.innerHTML =
        '<a href="/">Home</a>' +
        '<span aria-hidden="true">/</span>' +
        '<a href="/blogs/news">Journal</a>' +
        '<span aria-hidden="true">/</span>' +
        '<span>' + title.replace(/[<>]/g, '') + '</span>';
      var insertBefore = $('.longeva-article-eyebrow') || h1;
      insertBefore.parentNode.insertBefore(crumb, insertBefore);
    });

    // 5. Footer band: back-to-journal
    safe(function () {
      if (document.querySelector('.longeva-article-footer')) return;
      var body = getArticleBody();
      if (!body || !body.parentNode) return;
      var footer = document.createElement('div');
      footer.className = 'longeva-article-footer';
      footer.innerHTML =
        '<p class="longeva-article-footer__heading">Continue reading</p>' +
        '<p style="text-align:center;margin:0;">' +
          '<a class="longeva-article-footer__back" href="/blogs/news">Back to the journal</a>' +
        '</p>';
      body.parentNode.insertBefore(footer, body.nextSibling);
    });
  });
})();
